import { PAGE_SIZES } from 'src/constants/report-insights'
import { AlphaFeatureCollection } from 'src/types/map-editor'
import { Position } from '@turf/helpers'

type BaseImageElement = {
  image: string
}
export type AlphaElement = {
  type: 'alphamap'
} & BaseImageElement

export type ImageElement = {
  type: 'image'
} & BaseImageElement

export type MapElement = {
  type: 'map'
  featureCollection?: AlphaFeatureCollection
  mapCenter?: Position
  mapZoom?: number
} & BaseImageElement

export type TextElement = {
  type: 'text'
} & BaseImageElement

type HeaderElement = {
  type: 'header'
  title: {
    text: string
    fontSize: number
    fontColor: string
  }
  subtitle: {
    text: string
    fontSize: number
    fontColor: string
  }
}
type FooterElement = {
  type: 'footer'
  logo: string
  color: string
  pageNumber: number
}
type AllElements = ImageElement | HeaderElement | FooterElement

// Report Preview Widgets
type SectionElements =
  | Omit<ImageElement, 'image'>
  | Omit<MapElement, 'image'>
  | Omit<TextElement, 'image'>
  | Omit<AlphaElement, 'image'>
type WidgetProperties = {
  id: string
  visible: boolean
  label: string
  image?: string
}
// Either an image or a map +  widget properties
export type InsightWidget = SectionElements & WidgetProperties

export type PageElement = {
  xPosition: number
  yPosition: number

  width: number
  height: number
} & AllElements

export type PageRenderParams = {
  pageSize: keyof typeof PAGE_SIZES
  pageDimensions: {
    width: number
    height: number
  }
  pageColor: string
  orientation: 'portrait' | 'landscape'
  pageMargin: number
  coverPage?: {
    title: string
    subtitle: string
    logo: string
    color: string
  }
  logoDimensions: {
    width: number
    height: number
  }
  pageHeader?: {
    title: {
      text: string
      fontSize: number
      fontColor: string
    }
    subtitle: {
      text: string
      fontSize: number
      fontColor: string
    }
    height: number
  }
  pageFooter?: {
    logo: string
    color: string
    height: number
  }
  images: Array<string | undefined>
}

export type PagesRenderConfig = {
  page: {
    size: keyof typeof PAGE_SIZES
    width: number
    height: number
    color: string
    orientation: 'portrait' | 'landscape'
  }
  coverPage?: {
    title: string
    subtitle: string
    logo: string
    color: string
  }
  logoDimensions: {
    width: number
    height: number
  }
  pages: PageElement[][]
}

export type RenderPDFMsgStart = {
  type: 'render-pdf-start'
  payload: PagesRenderConfig
}

export type RenderPDFMsgComplete = {
  type: 'render-pdf-complete'
  payload: ArrayBuffer
}

export type RenderPDFMsgError = {
  type: 'render-pdf-error'
  payload: string
}

export type RenderPDFMsgProgress = {
  type: 'render-pdf-progress'
  payload: number
}

export type RenderPDFMsg = RenderPDFMsgStart | RenderPDFMsgComplete | RenderPDFMsgError

export function isRenderPDFStartMsg(data: unknown): data is RenderPDFMsgStart {
  return (data as RenderPDFMsgStart)?.type === 'render-pdf-start'
}

export function isRenderPDFMsgProgress(data: unknown): data is RenderPDFMsgProgress {
  return (data as RenderPDFMsgProgress)?.type === 'render-pdf-progress'
}

export function isRenderPDFCompleteMessage(data: unknown): data is RenderPDFMsgComplete {
  return (data as RenderPDFMsgComplete)?.type === 'render-pdf-complete'
}

export function isRenderPDFErrorMessage(data: unknown): data is RenderPDFMsgError {
  return (data as RenderPDFMsgError)?.type === 'render-pdf-error'
}
